import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
    ArrowLeft,
    Save,
    Upload,
    X,
    Star,
    Plus,
    Loader2,
    Image as ImageIcon,
    Type,
    FileText,
    Tag as TagIcon
} from 'lucide-react';

export const EntryForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);
    const [uploading, setUploading] = useState(false);

    const [formData, setFormData] = useState({
        term: '',
        type: '',
        definition: '',
        full_definition: '',
        tags: [] as string[],
        letter: '',
        image_url: '',
        is_featured: false
    });

    const [newTag, setNewTag] = useState('');
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            fetchEntry();
        }
    }, [id]);

    // Sync image preview with URL field
    useEffect(() => {
        setImagePreview(formData.image_url || null);
    }, [formData.image_url]);

    const fetchEntry = async () => {
        const { data, error } = await supabase
            .from('dictionary_entries')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            console.error('Error fetching entry:', error);
            navigate('/admin');
        } else {
            setFormData(data);
            if (data.image_url) {
                setImagePreview(data.image_url);
            }
        }
        setFetching(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
        setFormData(prev => {
            const newData = { ...prev, [name]: val };
            // Détecter automatiquement la lettre
            if (name === 'term' && typeof val === 'string' && val.length > 0 && name === 'term') {
                newData.letter = val[0].toUpperCase();
            }
            return newData;
        });
    };

    const addTag = () => {
        if (newTag && !formData.tags.includes(newTag)) {
            setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
            setNewTag('');
        }
    };

    const removeTag = (tagToRemove: string) => {
        setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `entries/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('entry-images')
            .upload(filePath, file);

        if (uploadError) {
            alert('Erreur lors de l\'upload : ' + uploadError.message);
        } else {
            const { data } = supabase.storage
                .from('entry-images')
                .getPublicUrl(filePath);

            setFormData(prev => ({ ...prev, image_url: data.publicUrl }));
            setImagePreview(data.publicUrl);
        }
        setUploading(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const { error } = id
            ? await supabase.from('dictionary_entries').update(formData).eq('id', id)
            : await supabase.from('dictionary_entries').insert([formData]);

        if (error) {
            alert('Erreur lors de l’enregistrement : ' + error.message);
        } else {
            navigate(-1);
        }
        setLoading(false);
    };

    if (fetching) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="animate-spin text-indigo-600" size={32} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-12">
            <div className="max-w-4xl mx-auto px-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-8 transition-colors group"
                >
                    <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                    Retour au dashboard
                </button>

                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Colonne de gauche : Champs texte */}
                        <div className="flex-1 space-y-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-4">
                                <div className="flex items-center gap-2 mb-2 text-indigo-600 font-semibold">
                                    <Type size={18} />
                                    Informations de base
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Terme</label>
                                    <input
                                        type="text"
                                        name="term"
                                        required
                                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={formData.term}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Type (ex: n. m.)</label>
                                        <input
                                            type="text"
                                            name="type"
                                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Lettre</label>
                                        <input
                                            type="text"
                                            name="letter"
                                            required
                                            maxLength={1}
                                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none uppercase text-center font-bold"
                                            value={formData.letter}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                name="is_featured"
                                                className="sr-only peer"
                                                checked={formData.is_featured}
                                                onChange={handleInputChange}
                                            />
                                            <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                        </div>
                                        <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors flex items-center gap-1.5">
                                            <Star size={14} className={formData.is_featured ? 'fill-amber-400 text-amber-400' : 'text-slate-400'} />
                                            Mettre en vedette sur la page d'accueil
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-4">
                                <div className="flex items-center gap-2 mb-2 text-indigo-600 font-semibold">
                                    <FileText size={18} />
                                    Définitions
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Définition courte</label>
                                    <textarea
                                        name="definition"
                                        required
                                        rows={3}
                                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                                        value={formData.definition}
                                        onChange={handleInputChange}
                                    ></textarea>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Définition complète</label>
                                    <textarea
                                        name="full_definition"
                                        required
                                        rows={6}
                                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={formData.full_definition}
                                        onChange={handleInputChange}
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Colonne de droite : Image et Tags */}
                        <div className="w-full md:w-80 space-y-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                                <div className="flex items-center gap-2 mb-4 text-indigo-600 font-semibold">
                                    <ImageIcon size={18} />
                                    Illustration
                                </div>

                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Lien vers l'image (URL externe)</label>
                                    <input
                                        type="text"
                                        name="image_url"
                                        placeholder="https://images.unsplash.com/..."
                                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                    />
                                    <p className="mt-1.5 text-[10px] text-slate-400 italic">Collez un lien direct vers une image ou utilisez l'upload ci-dessous.</p>
                                </div>

                                <div
                                    className="aspect-square bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center overflow-hidden relative group"
                                >
                                    {imagePreview ? (
                                        <>
                                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <label className="cursor-pointer bg-white text-slate-900 px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2">
                                                    <Upload size={14} /> Modifier
                                                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                                </label>
                                            </div>
                                        </>
                                    ) : (
                                        <label className="cursor-pointer flex flex-col items-center gap-2 text-slate-400 hover:text-indigo-500 transition-colors">
                                            {uploading ? <Loader2 className="animate-spin" /> : <Upload size={32} />}
                                            <span className="text-xs font-medium">Uploader une image</span>
                                            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                        </label>
                                    )}
                                </div>
                                {uploading && <p className="text-center text-xs text-indigo-600 mt-2">Upload en cours...</p>}
                            </div>

                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                                <div className="flex items-center gap-2 mb-4 text-indigo-600 font-semibold">
                                    <TagIcon size={18} />
                                    Tags
                                </div>

                                <div className="flex flex-wrap gap-2 mb-4">
                                    {formData.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs font-medium"
                                        >
                                            {tag}
                                            <button type="button" onClick={() => removeTag(tag)} className="hover:text-indigo-800">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Nouveau tag..."
                                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                    />
                                    <button
                                        type="button"
                                        onClick={addTag}
                                        className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                                    >
                                        <Plus size={18} />
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || uploading}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-2xl shadow-xl shadow-indigo-100 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-70"
                            >
                                {loading ? <Loader2 className="animate-spin" /> : <Save size={20} />}
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
